import { DateTime } from "luxon";

import { graphql } from "lib/generated/app-service-gql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const contactsCreditUsageHistoryQuery = graphql(`
  query contactsCreditUsageHistory {
    contactsCreditUsageHistory {
      months {
        periodStart
        creditsUsed
      }
      totalUsed
    }
  }
`);

/** Months the chart covers, including the current (partial) one. Matches the server's window. */
const USAGE_HISTORY_MONTHS = 12;

export type ContactsCreditUsageMonth = {
  /** UTC month start as an ISO date, e.g. "2026-08-01". */
  periodStart: string;
  /** Axis label, e.g. "Aug 26". */
  label: string;
  /** Full label for tooltips and the table, e.g. "August 2026". */
  longLabel: string;
  creditsUsed: number;
};

export type ContactsCreditUsageHistory = {
  /** Always `USAGE_HISTORY_MONTHS` entries, oldest first, gaps zero-filled. */
  months: ContactsCreditUsageMonth[];
  totalUsed: number;
  isLoading: boolean;
  isError: boolean;
};

/**
 * The company's CONTACTS credit spend by calendar month over the last year.
 *
 * The server omits months with no spend; a chart needs a continuous axis, so the window is rebuilt
 * here and the returned buckets slotted into it. UTC throughout to line up with the server's
 * `date_trunc` boundaries — a local-time month start would misfile spend either side of midnight.
 *
 * Who spent them is a separate, admin-only query — see `useContactsCreditSpendByUser`.
 */
export function useContactsCreditUsageHistory(): ContactsCreditUsageHistory {
  const { data, isError, isLoading } = useGraphQlQuery(
    ["contactsCreditUsageHistory"],
    contactsCreditUsageHistoryQuery,
    [undefined],
    REACT_QUERY_OPTIONS_NEVER_REFETCH,
  );

  const usedByMonth = new Map(
    (data?.contactsCreditUsageHistory?.months ?? []).map((month) => [
      month.periodStart,
      month.creditsUsed,
    ]),
  );

  const currentMonth = DateTime.utc().startOf("month");
  const months = Array.from({ length: USAGE_HISTORY_MONTHS }, (_, index) => {
    const month = currentMonth.minus({ months: USAGE_HISTORY_MONTHS - 1 - index });
    const periodStart = month.toISODate() ?? "";
    return {
      periodStart,
      label: month.toFormat("LLL yy"),
      longLabel: month.toFormat("LLLL yyyy"),
      creditsUsed: usedByMonth.get(periodStart) ?? 0,
    };
  });

  return {
    months,
    totalUsed: data?.contactsCreditUsageHistory?.totalUsed ?? 0,
    isLoading,
    isError,
  };
}
